import React, { useCallback, useContext, useEffect, useState } from "react";
import { CtaCustomData } from "../CtasContainer/CtasContainer";
import { PageStaticDataContext } from "../../../../contexts/PageStaticDataContextProvider";
import { ProductContext } from "../../../../contexts/ProductContextProvider";
import { BffDataContext } from "../../../../contexts/BffDataContextProvider";
import { PluginDataContext } from "../../../../pluginsInfrastructure/PluginDataContextProvider";
import { getCtaOnClickHandler, getProductCtaInfo } from "../../utils/ctaHelpers";
import { analyticsPageData } from "../../../../utils/analyticsConfig";
import { CtaButton } from "./CtaButton";
import { IdentityContext } from "../../../../contexts/IdentityProvider";
import { SignInModal } from "./SignInModal";
import TrackingUtil from "../../../../utils/tracking";
import { useExperimentsContext } from "../../../../contexts/ExperimentsProvider";
import { PluginExperimentContext } from "../../../../pluginsInfrastructure/PluginRegistryTypes";

export interface SignInCtaProps {
  customData: CtaCustomData;
}
const SIGN_IN_MODAL_EXPERIMENT_KEY = "pdp_albelli_signin_gate";
const SIGN_IN_MODAL_CONTROL_VARIATION = "control";
const SIGN_IN_MODAL_TEST_VARIATION = "test";
const SIGN_IN_MODAL_EXPERIMENT_CONTEXT: PluginExperimentContext = {
  experimentKey: SIGN_IN_MODAL_EXPERIMENT_KEY,
  renderTestExperienceForVariations: [
    SIGN_IN_MODAL_CONTROL_VARIATION,
    SIGN_IN_MODAL_TEST_VARIATION,
  ],
  fireImpressionForVariations: [SIGN_IN_MODAL_CONTROL_VARIATION, SIGN_IN_MODAL_TEST_VARIATION],
};
const SignInCta: React.FC<SignInCtaProps> = props => {
  const { pluginData } = useContext(PluginDataContext);
  const { auth, identity, isIdentityInitialized } = useContext(IdentityContext);
  const { mpv, ctaOverrideEntries, productBuilderOptions } = useContext(PageStaticDataContext);
  const { useExperiment } = useExperimentsContext();
  const { selections, selectedQuantity, initialSelections, initialQuantity } = useContext(
    ProductContext
  );
  const { temporarilyOutOfStock, prices, initialPrices, compatibleRanges } = useContext(
    BffDataContext
  );
  const [isModalOpened, setIsModalOpened] = useState<boolean>(false);
  const [shouldShowSignInModal, setShouldShowSignInModal] = useState<boolean>(false);
  const {
    customData: {
      flow: { type, url, context },
      isPrimaryCta,
      buttonIndex,
      showDescription,
      isDesignUploaded,
      isLastCta,
    },
  } = props;

  const ctaEnabled = !temporarilyOutOfStock && url;

  const { actualCtaText, actualCtaDescription, actualCtaIcon } = getProductCtaInfo(
    pluginData,
    ctaOverrideEntries,
    type,
    isDesignUploaded,
    isPrimaryCta
  );
  useEffect(() => {
    if (isIdentityInitialized && !identity?.isSignedIn) {
      const experimentVariation = useExperiment(SIGN_IN_MODAL_EXPERIMENT_CONTEXT);
      setShouldShowSignInModal(experimentVariation === "test");
    }
  }, [isIdentityInitialized, identity]);

  const trackModalEvent = (eventName: TRACKING_EVENTS): void => {
    TrackingUtil.trackExperimentClicked(mpv, {
      label: eventName,
      pageSection: "ADP",
      pageStage: "Design",
      variationName: SIGN_IN_MODAL_TEST_VARIATION,
      experimentName: SIGN_IN_MODAL_EXPERIMENT_KEY,
    });
  };

  const signIn = (): void => {
    if (!auth) {
      return;
    }
    trackModalEvent(TRACKING_EVENTS.SIGNIN_ACTION);
    auth.signIn();
  };

  const onClickHandler = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (shouldShowSignInModal) {
        trackModalEvent(TRACKING_EVENTS.SIGNIN_MODAL_VIEWED);
        setIsModalOpened(true);
      }
      getCtaOnClickHandler({
        event,
        mpv,
        selections: selections || {},
        initialSelections,
        pageSection: analyticsPageData.pageSection,
        position: buttonIndex + 1,
        url,
        linkType: type,
        initialQuantity,
        selectedQuantity,
        prices,
        productBuilderOptions,
        initialPrices,
        flowContexts: context,
        isDesignUploaded,
        compatibleRanges,
      });
    },
    [
      mpv,
      selections,
      initialSelections,
      analyticsPageData,
      buttonIndex,
      url,
      type,
      getCtaOnClickHandler,
      initialQuantity,
      selectedQuantity,
      prices,
      initialPrices,
      productBuilderOptions,
      shouldShowSignInModal,
    ]
  );
  const linkProperties = shouldShowSignInModal ? undefined : { href: url, openInNewTab: false };
  return (
    <>
      <SignInModal
        isOpen={isModalOpened}
        onSignInHandler={signIn}
        onSignInLaterHandler={(): void => {
          trackModalEvent(TRACKING_EVENTS.SIGNIN_LATER_ACTION);
        }}
        onDismissModalHandler={(): void => {
          setIsModalOpened(false);
          trackModalEvent(TRACKING_EVENTS.SIGNIN_MODAL_CLOSED);
        }}
        labels={{
          title: getDictionaryEntryValue("signInModalTitle"),
          description: getDictionaryEntryValue("signInDescription"),
          primaryButton: getDictionaryEntryValue("signInModalPrimaryAction"),
          secondaryButton: getDictionaryEntryValue("signInModalSecondaryAction"),
        }}
        url={url}
      />
      <CtaButton
        onClickHandler={onClickHandler}
        disabled={!ctaEnabled}
        linkProperties={linkProperties}
        buttonKey={type}
        text={actualCtaText}
        description={showDescription ? actualCtaDescription : ""}
        iconComponent={actualCtaIcon}
        pageSection={analyticsPageData.pageSection}
        textButtonProps={{
          skin: isPrimaryCta ? "primary" : "secondary",
        }}
        addBottomSpace={!isLastCta}
        position={buttonIndex}
      />
    </>
  );

  function getDictionaryEntryValue(key: string): string {
    return pluginData.contentful[key] || "";
  }
};

enum TRACKING_EVENTS {
  SIGNIN_MODAL_VIEWED = "Sign-in Modal View",
  SIGNIN_MODAL_CLOSED = "Sign-in Modal Close",
  SIGNIN_ACTION = "Sign-in Action",
  SIGNIN_LATER_ACTION = "Sign-in Later Action",
}

export default SignInCta;
